import { Button } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import type { Dispatch, SetStateAction } from "react";
import { useCallback, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useToast } from "../../../mantine/notifications/useToast";
import type { SubMeteringSystem } from "../../../types/api.types";
import type { Site } from "../../../utils/backend-types";
import { AutoImportNotificationMsg } from "../AutoImportNotificationMsg/AutoImportNotificationMsg";
import type { SelectedMeter } from "../EditConfigurationModal/EditConfigurationModal";
import { updateMeterParametersCache } from "../hooks/useMeterParameters";
import { MeterConfiguration } from "../MeterConfiguration/MeterConfiguration";
import { MetersSaveWarningModal } from "../MeterConfiguration/MetersSaveWarningModal/MetersSaveWarningModal";
import { checkSubMeteringConnection } from "../utils/checkSubMeteringConnection";
import { showSaveMeterErrorToast } from "../utils/showSaveMeterErrorToast";
import { updateEntitiesMeterData } from "../utils/updateEntitiesMeterData";

interface MeterSelectionProps {
  sites: Array<Site>;
  systemName: SubMeteringSystem;
  configurationId: number;
  buttonContainer?: HTMLElement | null;
  selectedMeters: Array<SelectedMeter>;
  isSelectedMetersWarningModalOpen: boolean;
  setIsSelectedMetersWarningModalOpen: Dispatch<SetStateAction<boolean>>;
  setSelectedMeters: Dispatch<SetStateAction<SelectedMeter[]>>;
  onNextStep: () => void;
  onToggle: () => void;
}

function MeterSelection({
  sites,
  systemName,
  configurationId,
  buttonContainer,
  selectedMeters,
  isSelectedMetersWarningModalOpen,
  setIsSelectedMetersWarningModalOpen,
  setSelectedMeters,
  onNextStep,
  onToggle
}: MeterSelectionProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { showToast, showErrorToast } = useToast();

  const [connectionLoading, setConnectionLoading] = useState(false);

  async function handleSubmitSelection(): Promise<void> {
    if (selectedMeters.length > 0) {
      const { success } = await handleSaveMeters();

      if (success) {
        return onNextStep();
      }

      return;
    }

    return onNextStep();
  }

  async function handleCheckSubMeteringConnection({
    withSuccessToast
  }: {
    withSuccessToast: boolean;
  }): Promise<boolean | void> {
    try {
      setConnectionLoading(true);
      const checkConnectionRes =
        await checkSubMeteringConnection(configurationId);

      if (checkConnectionRes.data?.error) {
        throw new Error(
          checkConnectionRes.data?.error ||
            t("errors.ThirdPartySystems.AuthenticationFailed")
        );
      }

      if (withSuccessToast) {
        showToast({
          type: "success",
          message: t("success.ThirdPartySystems.ConnectionSuccessful")
        });
      }

      return true;
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : typeof error === "string"
            ? error
            : t("errors.ThirdPartySystems.AuthenticationFailed");

      showErrorToast(errorMessage);
    } finally {
      setConnectionLoading(false);
    }
  }

  const handleSaveMeters = useCallback(
    async (singleEntityId?: number) => {
      try {
        if (singleEntityId) {
          const singleSelectedMeter = selectedMeters.find(
            (selectedMeter) =>
              selectedMeter.subMeteringSystemEntityId === singleEntityId
          );

          const meterId = singleSelectedMeter?.meterId ?? null;

          await updateEntitiesMeterData(configurationId, [
            {
              entityId: singleEntityId,
              meterId: meterId
            }
          ]);

          setSelectedMeters((prevSelectedMeters) => {
            return prevSelectedMeters.filter(
              (meter) => meter.subMeteringSystemEntityId !== singleEntityId
            );
          });

          updateMeterParametersCache(
            singleEntityId,
            { meter: meterId },
            queryClient,
            configurationId
          );

          return { success: true };
        }

        await updateEntitiesMeterData(
          configurationId,
          selectedMeters.map((selectedMeter) => ({
            entityId: selectedMeter.subMeteringSystemEntityId,
            meterId: selectedMeter.meterId
          }))
        );

        showToast({
          type: "success",
          message: (
            <AutoImportNotificationMsg selectedMeters={selectedMeters} />
          ),
          autoClose: false,
          withCloseButton: true
        });

        setSelectedMeters([]);

        return { success: true };
      } catch (error) {
        showSaveMeterErrorToast(showToast, error);
        return { success: false };
      }
    },
    [configurationId, queryClient, selectedMeters, setSelectedMeters, showToast]
  );

  function handleCloseSelectedMetersWarningModal() {
    setIsSelectedMetersWarningModalOpen(false);
  }

  return (
    <div className="MeterSelection">
      <MeterConfiguration
        configurationId={configurationId}
        connectionLoading={connectionLoading}
        selectedMeters={selectedMeters}
        setSelectedMeters={setSelectedMeters}
        sites={sites}
        systemName={systemName}
        onMetersSave={handleSaveMeters}
        onSubMeteringConnectionCheck={handleCheckSubMeteringConnection}
      />
      <MetersSaveWarningModal
        isOpen={isSelectedMetersWarningModalOpen}
        onCloseEditModal={onToggle}
        onCloseSelectedMetersWarningModal={
          handleCloseSelectedMetersWarningModal
        }
      />
      {buttonContainer && (
        <>
          {createPortal(
            <Button color="secondary" onClick={handleSubmitSelection}>
              Weiter
            </Button>,
            buttonContainer
          )}
        </>
      )}
    </div>
  );
}

export { MeterSelection };
